import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Icon, IconButton, Stack } from "@chakra-ui/react";
import { Bell, Flag, Link2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeAll } from "@/lib/offline";
import { prefetchUrgentDetails } from "@/lib/prefetch";
import { showToast } from "@/components/AppToaster";
import { urgentOrFilter, ago } from "@/lib/urgent";
import { colors } from "@/theme/colors";
import { logWarn } from "@/lib/log";


// Header bell for Admins/Supervisors: unresolved urgent items — auto-flagged
// submissions + open raised action items. Count clears as items are resolved.
export const NotificationBell = () => {
  const navigate = useNavigate();
  const { user, role, activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  // Full set of currently-open urgent ids. Unread is accounted over THIS set,
  // not the 15-item display window — a windowed unread count made the badge
  // move the wrong way when resolves pulled unseen overflow items into view.
  const [openIds, setOpenIds] = useState(() => new Set());
  const openIdsRef = useRef(new Set());
  // "Seen" is per-device read state: viewing a record removes it from the
  // badge count while it stays open until actually resolved.
  const [seen, setSeen] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const canSee = ["org_admin", "supervisor"].includes(role) && activeCompanyId;

  useEffect(() => {
    if (!user) return;
    const saved = cacheGet(user.id, "bell_seen");
    if (Array.isArray(saved)) setSeen(new Set(saved));
  }, [user]);

  const markSeen = useCallback((id) => {
    // Only open urgent items participate in unread accounting — viewing an
    // already-resolved or non-urgent record must not shift the numbers.
    if (!openIdsRef.current.has(id)) return;
    setSeen((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev).add(id);
      cachePut(user.id, "bell_seen", [...next]);
      return next;
    });
  }, [user.id]);

  const load = useCallback(async () => {
    if (!canSee) return;
    const [listRes, idsRes, countRes] = await safeAll([
      // Display window: 15 most recent, filtered server-side.
      supabase.from("submissions")
        .select("id,template_title,template_type,worker_name,location_name,created_at,flagged,link_context,parent_submission_id")
        .eq("company_id", activeCompanyId).eq("status", "pending").or(urgentOrFilter())
        .order("created_at", { ascending: false }).limit(15),
      // Full open set (ids only) — the basis for unread accounting.
      supabase.from("submissions").select("id")
        .eq("company_id", activeCompanyId).eq("status", "pending").or(urgentOrFilter())
        .limit(2000),
      // True open total.
      supabase.from("submissions").select("id", { count: "exact", head: true })
        .eq("company_id", activeCompanyId).eq("status", "pending").or(urgentOrFilter()),
    ]);
    if (listRes.error || listRes.data === null) {
      // Failed fetch is never "all clear": keep the badge backed by the
      // last-known-good snapshot instead of silently clearing it.
      const snap = cacheGet(user.id, "urgent");
      if (Array.isArray(snap)) setItems(snap);
      const cachedIds = cacheGet(user.id, "urgent_ids");
      if (Array.isArray(cachedIds)) {
        const s = new Set(cachedIds);
        setOpenIds(s);
        openIdsRef.current = s;
      }
      const cnt = cacheGet(user.id, "urgent_count");
      if (typeof cnt === "number") setOpenCount(cnt);
      return;
    }
    const next = listRes.data || [];
    const ids = new Set((idsRes.data || []).map((r) => r.id));
    const total = countRes.error ? ids.size : (countRes.count ?? ids.size);
    setItems(next);
    setOpenIds(ids);
    openIdsRef.current = ids;
    setOpenCount(total);
    cachePut(user.id, "urgent", next);
    cachePut(user.id, "urgent_ids", [...ids]);
    cachePut(user.id, "urgent_count", total);
    // Drop "seen" marks for items that are no longer open (resolved) —
    // the resolved item leaves BOTH the open and seen sets together, so
    // unread stays put for read items and drops by one for unread items.
    setSeen((prev) => {
      const kept = [...prev].filter((id) => ids.has(id));
      if (kept.length === prev.size) return prev;
      cachePut(user.id, "bell_seen", kept);
      return new Set(kept);
    });
    // Warm each displayed item's full detail snapshot so a tap works offline,
    // including alerts that arrived after login (outside the login prefetch).
    prefetchUrgentDetails(supabase, user.id, next).catch(() => {});
  }, [canSee, activeCompanyId, user.id]);

  // Self-correct after reconnect (e.g. leaving airplane mode) or returning to
  // the foreground instead of waiting for the next poll — keeps the count
  // live-accurate in BOTH directions even if the realtime socket died.
  useEffect(() => {
    if (!canSee) return;
    const onOnline = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [canSee, load]);

  // Instant badge feedback from the detail page: viewing marks the item seen,
  // a status change (resolve) refreshes the list — no reliance on realtime.
  useEffect(() => {
    if (!canSee) return;
    const onViewed = (e) => markSeen(e.detail.id);
    const onChanged = () => load();
    window.addEventListener("hakina:submission-viewed", onViewed);
    window.addEventListener("hakina:submission-status-changed", onChanged);
    return () => {
      window.removeEventListener("hakina:submission-viewed", onViewed);
      window.removeEventListener("hakina:submission-status-changed", onChanged);
    };
  }, [canSee, markSeen, load]);

  useEffect(() => {
    if (!canSee) return;
    load();
    const poll = setInterval(load, 300000); // slow fallback if the socket drops
    // Realtime push: private broadcast topic fed by a DB trigger on submissions.
    let ch;
    let cancelled = false;
    (async () => {
      await supabase.realtime.setAuth(); // private channels need the user JWT
      if (cancelled) return;
      ch = supabase
        .channel(`urgent:${activeCompanyId}`, { config: { private: true } })
        .on("broadcast", { event: "urgent" }, ({ payload }) => {
          load();
          // Let any open view (e.g. the Urgent Items queue) refresh too,
          // without each page needing its own socket.
          window.dispatchEvent(new Event("hakina:urgent-refresh"));
          if (payload?.kind === "new" && payload?.status === "pending") {
            showToast(payload.flagged
              ? `Flagged report: ${payload.template_title} — ${payload.worker_name}`
              : `${payload.template_type} raised — ${payload.worker_name}`);
          }
        })
        .subscribe((status, err) => {
          if (status !== "SUBSCRIBED") logWarn("urgent-bell realtime:", status, err?.message || "");
        });
    })();
    return () => { cancelled = true; clearInterval(poll); if (ch) supabase.removeChannel(ch); };
  }, [canSee, activeCompanyId, load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    // pointerdown (not mousedown): fires reliably for touch on iOS/Android PWAs,
    // so a tap on a list item is never mistaken for an outside tap.
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  if (!canSee) return null;

  // Unread = every open urgent item this user hasn't viewed — accounted over
  // the FULL open set (seen ∩ open), never a display window.
  const seenOpen = [...seen].filter((id) => openIds.has(id)).length;
  const unseenCount = Math.max(openCount, openIds.size) - seenOpen;

  return (
    <Box position="relative" ref={boxRef}>
      <IconButton aria-label="Urgent notifications" variant="ghost" size="sm"
        onClick={() => { setOpen((o) => !o); if (!open) load(); }} data-testid="notification-bell">
        <Bell size={19} color={colors.textPrimary} />
      </IconButton>
      {unseenCount > 0 && (
        <Flex position="absolute" top="0" right="0" minW="17px" h="17px" px="1" align="center" justify="center"
          bg={colors.poppy} color="white" borderRadius="full" fontSize="10px" fontWeight="700"
          pointerEvents="none" data-testid="notification-badge">
          {unseenCount}
        </Flex>
      )}

      {open && (
        <NotificationPanel items={items} seen={seen} unseenCount={unseenCount}
          openCount={Math.max(openCount, items.length)}
          onOpenItem={(id) => { markSeen(id); setOpen(false); navigate(`/submissions/${id}`); }}
          onViewAll={() => { setOpen(false); navigate("/urgent"); }} />
      )}
    </Box>
  );
};

const NotificationPanel = ({ items, seen, unseenCount, openCount, onOpenItem, onViewAll }) => (
  <Box position="absolute" right="0" top="calc(100% + 8px)" w={{ base: "82vw", sm: "360px" }} maxH="420px"
    overflowY="auto" bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px"
    boxShadow="0 16px 40px rgba(0,0,0,0.14)" zIndex={1400} css={{ animation: "qaFadeIn 0.15s ease" }}
    data-testid="notification-panel">
    <Flex px="4" py="3" borderBottom="1px solid" borderColor={colors.border} justify="space-between" align="center">
      <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Urgent items</Text>
      <Text fontSize="xs" color={colors.textSecondary} data-testid="notification-open-count">
        {unseenCount} unread · {openCount} open
      </Text>
    </Flex>
    {items.length === 0 ? (
      <Box px="4" py="6" textAlign="center" data-testid="notification-empty">
        <Text fontSize="sm" color={colors.textSecondary}>All clear — no flagged reports or open action items.</Text>
      </Box>
    ) : (
      <Stack gap="0">
        {items.map((s) => <NotificationItem key={s.id} s={s} isSeen={seen.has(s.id)} onOpen={() => onOpenItem(s.id)} />)}
        <Flex as="button" type="button" w="full" justify="center" px="4" py="3"
          cursor="pointer" touchAction="manipulation" _hover={{ bg: colors.cream }}
          onClick={onViewAll} data-testid="notification-view-all">
          <Text fontSize="sm" fontWeight="600" color={colors.tealHover}>
            {openCount > items.length
              ? `+${openCount - items.length} more — open the urgent queue`
              : "Open the urgent queue"}
          </Text>
        </Flex>
      </Stack>
    )}
  </Box>
);

const NotificationItem = ({ s, isSeen, onOpen }) => {
  const isFlag = s.flagged;
  return (
    <Flex as="button" type="button" w="full" textAlign="left" gap="3" px="4" py="3"
      borderBottom="1px solid" borderColor={colors.border} _hover={{ bg: colors.cream }}
      cursor="pointer" touchAction="manipulation" opacity={isSeen ? 0.65 : 1}
      onClick={onOpen} data-testid={`notification-item-${s.id}`}>
      <Flex align="center" justify="center" w="30px" h="30px" borderRadius="8px" flexShrink={0}
        bg={isFlag ? colors.criticalBg : colors.poppyTint}>
        <Icon as={isFlag ? Flag : Link2} boxSize="3.5" color={colors.poppy} />
      </Flex>
      <Box minW="0" flex="1">
        <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} lineClamp={1}>
          {isFlag ? `Flagged: ${s.template_title}` : `${s.template_type} raised`}
        </Text>
        <Text fontSize="xs" color={colors.textSecondary} lineClamp={1}>
          {s.worker_name} · {s.location_name}{!isFlag && s.link_context?.parent_title ? ` · from ${s.link_context.parent_title}` : ""}
        </Text>
        <Text fontSize="10px" color={colors.textSecondary}>{ago(s.created_at)}</Text>
      </Box>
      {!isSeen && (
        <Box w="8px" h="8px" mt="1.5" borderRadius="full" bg={colors.poppy} flexShrink={0}
          data-testid={`notification-unread-dot-${s.id}`} />
      )}
    </Flex>
  );
};
